import * as THREE from 'three';
import type { Human } from '../human/types';
import { Rig } from './Rig';
import { FaceMixer } from './Face';
import { MOODS, REACTIONS, type Mood } from './moods';
import type { FaceUnits } from './FaceUnits';

const X = new THREE.Vector3(1, 0, 0);

const Z = new THREE.Vector3(0, 0, 1);
const DOWN = new THREE.Vector3(0, -1, 0);
const FWD = Z;
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _ray = new THREE.Raycaster();
const _e = new THREE.Euler();

/** Ressort critique (lissage naturel, sans dépassement). */
class Spring {
  v = 0;
  constructor(public x = 0, public omega = 8) {}
  step(target: number, dt: number) {
    const w = this.omega, f = 1 + 2 * dt * w, oo = w * w, hoo = dt * oo, hhoo = dt * hoo;
    const det = 1 / (f + hhoo);
    const x = (f * this.x + dt * this.v + hhoo * target) * det;
    this.v = (this.v + hoo * (target - this.x)) * det;
    this.x = x;
    return x;
  }
}

/** Bruit 1D lisse (somme de sinus incommensurables) — suffisant pour des oscillations organiques. */
const wander = (t: number, seed: number) =>
  (Math.sin(t * 0.37 + seed) * 0.5 + Math.sin(t * 0.61 + seed * 2.3) * 0.3 + Math.sin(t * 1.13 + seed * 4.1) * 0.2);

/** Réglages de la pose debout procédurale (modifiables à chaud en dev : dev.pose). */
export const POSE_TUNING = {
  armDown: 0.6, armFwd: -0.06, elbow: 0.1, forearmTwist: 0.6, wristX: 0, wristZ: -0.08,
  fingerBase: 0.05, fingerStep: 0.03, fingerJ: 0.08, thumb: 0.12, fingerSpread: 0.08,
};
const _Yl = new THREE.Vector3(0, 1, 0);

const HEAD_CHAIN: ReadonlyArray<readonly [string, number]> = [['spine01', 0.08], ['neck01', 0.17], ['neck02', 0.2], ['neck03', 0.2], ['head', 0.35]];

/**
 * Source de pose de base (poses, animations) : appelée en premier chaque frame, elle écrit des rotations
 * locales via rig.setPoseLocal / rig.setRootOffset. La présence (respiration, regard, clignements, humeur)
 * s'ajoute par-dessus. Sans source, la présence applique sa pose debout détendue procédurale.
 */
export interface PoseSource {
  apply(rig: Rig, dt: number): void;
  /** Balancement debout autorisé (désactivé pour assise / allongée). */
  readonly allowSway: boolean;
  /** Poids (0..1) laissé à la présence pour orienter tête/cou vers la cible du regard. */
  readonly gazeWeight: number;
}

type GazeMode = 'camera' | 'cursor' | 'aside';

/**
 * Le "moteur de présence" : tout ce qui la fait paraître vivante.
 * Respiration, micro-balancement, pose détendue, regard (contact visuel, suivi du curseur,
 * saccades, détournements pudiques), clignements, humeurs et réactions brèves.
 */
const SLEEP_FACE = { eyeBlinkLeft: 1, eyeBlinkRight: 1, jawOpen: 0.035, mouthClose: 0.02, browInnerUp: -0.05, eyeLookDownLeft: 0.2, eyeLookDownRight: 0.2 };

export class Presence {
  readonly rig: Rig;
  readonly face: FaceMixer;
  mood: Mood = MOODS.sereine;
  /** Taille de pupille (1 = neutre) : émotion, proximité, luminosité. Lue par le shader des yeux. */
  pupil = 1;
  /** Luminosité ambiante perçue (0..1), fournie par l'application selon l'ambiance. */
  ambientLight = 0.5;
  /** Distance caméra–tête (m), fournie par l'application. */
  viewerDistance = 2;
  /** Rougissement courant (0..1), lissé ; lu par le rendu de la peau. */
  blush = 0;
  private blushTarget = 0;
  private blushUntil = 0;
  /** Unités faciales osseuses (chargées en différé) : pommettes, sillons, paupières inférieures… */
  faceUnits: FaceUnits | null = null;
  /** Pose/animation de base (null = pose debout détendue procédurale). */
  poseSource: PoseSource | null = null;
  /** Couches de rotations additionnelles (repère personnage), appliquées après la pose et le regard. */
  readonly layers: Array<(rig: Rig, dt: number) => void> = [];
  /** Regard forcé vers la caméra (mode photo : elle pose). */
  lookAtCamera = false;
  /** Sommeil (0 = éveillée, 1 = endormie) : paupières closes, respiration lente et profonde, tête relâchée. */
  sleep = 0;
  onMoodChange: (m: Mood) => void = () => {};

  private t = 0;
  // Respiration
  private breathPhase = 0;
  // Regard
  private gazeMode: GazeMode = 'camera';
  private cursorNdc = new THREE.Vector2();
  private cursorMovedAt = -100;
  private asideUntil = 0;
  private asideDir = new THREE.Vector3();
  private nextAside = 4;
  private gazeTarget = new THREE.Vector3();
  private eyePoint = new THREE.Vector3();
  private microOffset = new THREE.Vector3();
  private nextMicro = 0;
  private headYaw = new Spring(0, 5.5);
  private headPitch = new Spring(0, 5.5);
  private headRoll = new Spring(0, 3);
  private eyeOut = { yaw: 0, pitch: 0 };
  // Clignements
  private nextBlink = 1.5;
  private blinkStart = -1;
  private doubleBlink = false;
  // Réactions
  private reaction?: { name: string; start: number; duration: number };
  private glance?: { point: THREE.Vector3; until: number };
  private gesture?: { kind: 'shake' | 'nod'; start: number; dur: number; amp: number };
  private sighStart = -10;
  private laughLayer: Record<string, number> = {};
  // Repères
  private pivot = new THREE.Vector3();
  private headRest = new THREE.Vector3();
  private side = new Map<string, number>(); // .L → +1, .R → -1

  constructor(private human: Human, private camera: THREE.Camera, dom: HTMLElement) {
    this.rig = new Rig(human.root, human.bones);
    this.face = new FaceMixer(human);
    this.recalibrate();
    this.setMood('sereine');
    dom.addEventListener('pointermove', (e) => {
      const r = dom.getBoundingClientRect();
      this.cursorNdc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      this.cursorMovedAt = this.t;
    });
    dom.addEventListener('pointerleave', () => { this.cursorMovedAt = -100; });
  }

  /** À rappeler après un changement de morphologie (setParams). */
  recalibrate() {
    this.rig.captureRest();
    const ankleL = this.rig.get('foot.L')?.charP, ankleR = this.rig.get('foot.R')?.charP;
    if (ankleL && ankleR) this.pivot.copy(ankleL).add(ankleR).multiplyScalar(0.5);
    this.headRest.copy(this.rig.get('head')?.charP ?? _v.set(0, 1.55, 0));
    for (const name of this.rig.rest.keys()) this.side.set(name, name.endsWith('.L') ? 1 : name.endsWith('.R') ? -1 : 0);
  }

  setMood(id: keyof typeof MOODS | string) {
    const m = MOODS[id];
    if (!m) return;
    this.mood = m;
    this.face.setLayer('mood', m.face);
    this.onMoodChange(m);
  }

  /** Réapplique le visage de l'humeur courante (expressions re-validées), sans relancer ce qu'entraîne un changement d'humeur. */
  refreshFace() { this.face.setLayer('mood', this.mood.face); }

  /** Fait rougir (0..1) pendant `seconds` (le retour est lent, comme en vrai). */
  flush(level: number, seconds = 4) {
    this.blushTarget = Math.max(this.blushTarget, level);
    this.blushUntil = Math.max(this.blushUntil, this.t + seconds);
  }

  react(name: keyof typeof REACTIONS | string) {
    const r = REACTIONS[name];
    if (!r) return;
    if (name === 'rougir') this.flush(0.85, 5);
    else if (name === 'rire') this.flush(0.3, 3);
    else if (name === 'surprise') this.flush(0.25, 2);
    this.reaction = { name, start: this.t, duration: r.duration };
    this.face.setLayer('reaction', r.face, 0);
    if (Math.random() < 0.5) this.blinkStart = this.t; // un clignement accompagne souvent la surprise
  }

  /** Soupir : longue inspiration, paupières mi-closes, épaules qui retombent. */
  sigh() {
    this.sighStart = this.t;
  }

    /** Jette un regard vers un point (monde) pendant `duration` secondes, puis revient. */
  glanceAt(point: THREE.Vector3, duration = 0.9) {
    this.glance = { point: point.clone(), until: this.t + duration };
  }

  /** Petit geste de tête : "non" (shake) ou "oui" (nod). */
  gestureHead(kind: 'shake' | 'nod', amp = 0.1, dur = 0.8) {
    this.gesture = { kind, start: this.t, dur, amp };
  }

  get time() { return this.t; }

  update(dt: number) {
    this.t += dt;
    const t = this.t, m = this.mood, rig = this.rig;
    rig.begin();

    // ---------- Pose de base : source externe, sinon pose détendue procédurale ----------
    const src = this.poseSource;
    if (src) src.apply(rig, dt);
    else for (const s of ['L', 'R'] as const) {
      const sg = s === 'L' ? 1 : -1;
      const P = POSE_TUNING;
      rig.bendToward(`upperarm01.${s}`, DOWN, P.armDown);
      rig.bendToward(`upperarm01.${s}`, FWD, P.armFwd);
      rig.bendToward(`lowerarm01.${s}`, FWD, P.elbow);
      rig.rotateLocal(`lowerarm02.${s}`, _Yl, sg * P.forearmTwist);
      rig.rotate(`shoulder01.${s}`, Z, -sg * 0.05);           // épaules relâchées
      rig.rotateLocal(`wrist.${s}`, X, P.wristX);
      rig.rotateLocal(`wrist.${s}`, Z, sg * P.wristZ);
      for (let f = 2; f <= 5; f++) for (let j = 1; j <= 3; j++) rig.rotateLocal(`finger${f}-${j}.${s}`, X, P.fingerBase + P.fingerStep * f + (j === 1 ? 0 : P.fingerJ));
      for (let f = 2; f <= 5; f++) rig.rotateLocal(`finger${f}-1.${s}`, Z, sg * P.fingerSpread * (f - 3.5));
      rig.rotateLocal(`finger1-2.${s}`, X, P.thumb);
      rig.rotateLocal(`finger1-3.${s}`, X, P.thumb * 1.3);
      // Jambes : rapprocher légèrement les pieds.
      rig.rotate(`upperleg01.${s}`, Z, -sg * 0.075);
      rig.rotate(`foot.${s}`, Z, sg * 0.07);
    }

    // ---------- Respiration ----------
    const sl = this.sleep;
    this.breathPhase += dt * (m.breathRate * (1 - 0.42 * sl) / 60) * Math.PI * 2;
    const ph = this.breathPhase % (Math.PI * 2);
    // Inspiration ~40 %, expiration ~60 %, profil doux.
    const b = ph < Math.PI * 0.8 ? 0.5 - 0.5 * Math.cos(ph / 0.8) : 0.5 + 0.5 * Math.cos((ph - Math.PI * 0.8) / 1.2);
    // Soupir (2,4 s) : amplitude de respiration accrue + paupières lourdes + tête qui s'incline.
    const su = (t - this.sighStart) / 2.4;
    const sighEnv = su >= 0 && su < 1 ? Math.sin(su * Math.PI) : 0;
    const bd = b * m.breathDepth * (1 + 1.6 * sighEnv + 0.6 * sl) + sighEnv * 0.6;
    if (sighEnv > 0) {
      this.face.setLayer('sigh', { eyeBlinkLeft: 0.35, eyeBlinkRight: 0.35, browInnerUp: 0.15, mouthClose: 0.1 }, sighEnv);
    } else this.face.clearLayer('sigh');
    rig.rotate('spine03', X, -0.012 * bd);
    rig.rotate('spine02', X, -0.016 * bd);
    rig.rotate('spine01', X, -0.006 * bd);
    rig.rotate('neck01', X, 0.016 * bd);                       // compense pour garder la tête stable
    rig.rotate('clavicle.L', Z, 0.02 * bd);
    rig.rotate('clavicle.R', Z, -0.02 * bd);
    rig.rotate('breast.L', X, -0.03 * bd);
    rig.rotate('breast.R', X, -0.03 * bd);

    // ---------- Balancement (pendule inversé autour des chevilles) ----------
    const sw = (src && !src.allowSway ? 0 : m.sway) * (1 - sl);
    const roll = wander(t * 0.55, 1.7) * 0.012 * sw;
    const pitchSway = wander(t * 0.45, 4.2) * 0.008 * sw;
    // Avec une animation de base (pieds verrouillés par IK), on limite le lacet pour ne pas faire glisser les pieds.
    const yawSway = wander(t * 0.3, 7.7) * 0.03 * sw * (src ? 0.3 : 1);
    _q.setFromEuler(_e.set(pitchSway, yawSway, roll, 'YXZ'));
    rig.rotateQ('root', _q);
    const rootP = rig.get('root')?.charP;
    if (rootP && sw > 0) {
      _v.copy(rootP).sub(this.pivot).applyQuaternion(_q).add(this.pivot).sub(rootP);
      rig.rootOffset.add(_v);
    }
    // Le haut du corps compense une partie du roulis (équilibre).
    rig.rotate('spine04', Z, -roll * 0.6);
    rig.rotate('spine02', Z, -roll * 0.5);

    // ---------- Regard ----------
    this.updateGaze(dt);

    // ---------- Réaction en cours ----------
    let reactPitch = 0, reactTilt = 0;
    if (this.reaction) {
      const r = REACTIONS[this.reaction.name];
      const u = (t - this.reaction.start) / this.reaction.duration;
      const env = u < 0.12 ? u / 0.12 : u < 0.7 ? 1 : Math.max(0, 1 - (u - 0.7) / 0.3);
      this.face.setGain('reaction', env);
      // La réaction remplace l'expression d'humeur (pas d'addition, sinon les traits saturent).
      this.face.setGain('mood', 1 - 0.85 * env);
      reactPitch = (r.headPitch ?? 0) * env;
      reactTilt = (r.headTilt ?? 0) * env;
      if (this.reaction.name === 'rire') {
        // Un rire vit : "ha-ha" de la mâchoire (~4,5 Hz), épaules et tête qui sautillent, amplitude qui retombe.
        const elapsed = t - this.reaction.start;
        const pulse = 0.5 + 0.5 * Math.sin(elapsed * Math.PI * 2 * 4.5);
        const fade = env * Math.max(0, 1 - elapsed / (this.reaction.duration * 1.1));
        this.laughLayer['bu:JawDrop'] = 0.22 * pulse * fade;
        this.face.setLayer('laugh', this.laughLayer);
        reactPitch -= 0.025 * pulse * fade;
        rig.rotate('spine02', X, -0.012 * pulse * fade);
        rig.rotate('clavicle.L', Z, 0.02 * pulse * fade);
        rig.rotate('clavicle.R', Z, -0.02 * pulse * fade);
      }
      if (u >= 1) { this.face.clearLayer('reaction'); this.face.clearLayer('laugh'); this.face.setGain('mood', 1); this.reaction = undefined; }
    }

    // Tête : lacet/tangage répartis sur la nuque, roulis d'humeur.
    let yaw = this.headYaw.x, pitch = this.headPitch.x;
    if (this.gesture) {
      const g = this.gesture, u = (t - g.start) / g.dur;
      if (u >= 1) this.gesture = undefined;
      else {
        const osc = Math.sin(u * Math.PI * 2 * (g.kind === 'shake' ? 2.5 : 2)) * g.amp * Math.sin(u * Math.PI);
        if (g.kind === 'shake') yaw += osc; else pitch += Math.abs(osc);
      }
    }
    const rollHead = this.headRoll.step(m.headTilt + reactTilt + yaw * 0.12, dt);
        const headPitchTotal = pitch + m.headPitch + reactPitch;
    const gw = (src ? src.gazeWeight : 1) * (1 - sl);
    for (const [n, w] of HEAD_CHAIN) {
      _q.setFromEuler(_e.set(headPitchTotal * w * gw, yaw * w * gw, rollHead * w * gw, 'YXZ'));
      rig.rotateQ(n, _q);
    }

    // Couches additionnelles (réglages de posture…), au-dessus de la pose, de la respiration et du regard.
    for (const layer of this.layers) layer(rig, dt);

    // Muscles du visage (unités osseuses), poids lissés par le mixeur à la frame précédente.
    if (this.faceUnits) this.faceUnits.apply(rig, this.face.bones);

    rig.apply();

    // Yeux : visée exacte après la pose de la tête.
    this.human.root.updateMatrixWorld(true);
    rig.aim('eye.L', this.eyePoint, 0.55, this.eyeOut);
    rig.aim('eye.R', this.eyePoint, 0.55);

    // ---------- Paupières : clignements + suivi vertical du regard ----------
    this.updateBlink(dt);
    // Sommeil : paupières closes, visage détendu (bouche entrouverte), sourcils au repos.
    if (sl > 0.001) this.face.setLayer('sommeil', SLEEP_FACE, sl);
    else this.face.clearLayer('sommeil');

    this.face.update(dt);

    // Pupilles : se contractent à la lumière, se dilatent avec l'émotion et la proximité.
    const emo = m.id === 'joyeuse' ? 0.16 : m.id === 'timide' ? 0.14 : m.id === 'curieuse' ? 0.1 : m.id === 'reveuse' ? 0.08 : 0;
    const surprise = this.reaction?.name === 'surprise' ? 0.25 : 0;
    const near = THREE.MathUtils.clamp((1.4 - this.viewerDistance) / 0.9, 0, 1) * 0.15;
    const pupilGoal = 1.08 - 0.35 * this.ambientLight + emo + surprise + near;
    this.pupil += (pupilGoal - this.pupil) * (1 - Math.exp(-dt / (pupilGoal > this.pupil ? 0.9 : 0.35)));

        // Rougissement : monte vite (~0,6 s), redescend lentement (~4 s) ; fond léger selon l'humeur.
    if (t > this.blushUntil) this.blushTarget = 0;
    const base = m.id === 'timide' ? 0.35 : m.id === 'joyeuse' ? 0.12 : 0;
    const goal = Math.max(base, this.blushTarget);
    const k = goal > this.blush ? 1 - Math.exp(-dt / 0.6) : 1 - Math.exp(-dt / 4);
    this.blush += (goal - this.blush) * k;
  }

  private updateGaze(dt: number) {
    const t = this.t, m = this.mood;
    const root = this.human.root;
    // Choix du mode.
    const cursorActive = t - this.cursorMovedAt < 2.2;
    if (t > this.nextAside && this.gazeMode !== 'aside' && !cursorActive) {
      this.gazeMode = 'aside';
      const a = (Math.random() < 0.5 ? -1 : 1) * (0.35 + Math.random() * 0.45);
      const down = -0.15 - Math.random() * 0.25 * (1 + (m.id === 'timide' ? 1 : 0));
      this.asideDir.set(Math.sin(a), down, Math.cos(a)).normalize();
      this.asideUntil = t + 0.7 + Math.random() * 1.4;
      this.nextAside = t + (60 / Math.max(0.5, m.gazeAversion)) * (0.5 + Math.random());
    }
    if (this.gazeMode === 'aside' && (t > this.asideUntil || cursorActive)) this.gazeMode = cursorActive ? 'cursor' : 'camera';
    if (this.gazeMode !== 'aside') this.gazeMode = cursorActive ? 'cursor' : 'camera';
    // Elle pose (mode photo) : regard dans l'objectif, sans détour.
    if (this.lookAtCamera) { this.gazeMode = 'camera'; this.nextAside = Math.max(this.nextAside, t + 3); }

    // Cible en monde.
    const headWorld = _v2.copy(this.headRest).applyMatrix4(root.matrixWorld);
    const glancing = this.glance && t < this.glance.until;
    if (!glancing) this.glance = undefined;
    if (glancing) {
      this.gazeTarget.copy(this.glance!.point);
    } else if (this.gazeMode === 'camera') {
      this.camera.getWorldPosition(this.gazeTarget);
    } else if (this.gazeMode === 'cursor') {
      _ray.setFromCamera(this.cursorNdc, this.camera);
      const d = _ray.ray.origin.distanceTo(headWorld);
      this.gazeTarget.copy(_ray.ray.origin).addScaledVector(_ray.ray.direction, d * 0.55);
    } else {
      _v.copy(this.asideDir).transformDirection(root.matrixWorld);
      this.gazeTarget.copy(headWorld).addScaledVector(_v, 1.5);
    }

    // Micro-saccades : petits sauts permanents (les yeux ne sont jamais parfaitement fixes).
    if (t > this.nextMicro) {
      const amp = 0.006 + Math.random() * 0.01;
      this.microOffset.set((Math.random() - 0.5) * amp * 2, (Math.random() - 0.5) * amp, 0);
      this.nextMicro = t + 0.25 + Math.random() * 0.9;
    }
    // Les yeux rattrapent la cible très vite (saccade), la tête suit avec inertie.
    const target = _v.copy(this.gazeTarget).add(this.microOffset);
    const jump = this.eyePoint.distanceTo(target);
    const k = jump > 0.25 ? 1 - Math.exp(-dt / 0.03) : 1 - Math.exp(-dt / 0.06);
    if (jump > 0.6 && Math.random() < 0.02) this.blinkStart = t; // clignement sur grand déplacement du regard
    this.eyePoint.lerp(target, k);

    // Angles pour la tête, en repère personnage.
    _v.copy(this.gazeTarget);
    root.worldToLocal(_v).sub(this.headRest);
    const yawT = THREE.MathUtils.clamp(Math.atan2(_v.x, _v.z), -0.85, 0.85) * (this.gazeMode === 'aside' ? 0.5 : 0.75);
    const pitchT = THREE.MathUtils.clamp(-Math.atan2(_v.y, Math.hypot(_v.x, _v.z)), -0.35, 0.4) * 0.6;
    this.headYaw.step(yawT, dt);
    this.headPitch.step(pitchT, dt);
  }

  private updateBlink(_dt: number) {
    const t = this.t, m = this.mood;
    if (this.sleep > 0.6) this.nextBlink = Math.max(this.nextBlink, t + 0.5);
    if (t > this.nextBlink && this.blinkStart < 0) {
      this.blinkStart = t;
      this.doubleBlink = Math.random() < 0.12;
      // Intervalles irréguliers (log-normal approx.).
      const mean = 60 / Math.max(4, m.blinkRate);
      this.nextBlink = t + mean * Math.exp((Math.random() - 0.5) * 1.1);
    }
    let w = 0;
    if (this.blinkStart >= 0) {
      const u = t - this.blinkStart;
      const close = 0.075, hold = 0.035, open = 0.17;
      if (u < close) w = (u / close) ** 2;
      else if (u < close + hold) w = 1;
      else if (u < close + hold + open) { const v = (u - close - hold) / open; w = (1 - v) * (1 - v); }
      else {
        w = 0;
        if (this.doubleBlink) { this.doubleBlink = false; this.blinkStart = t + 0.08; }
        else this.blinkStart = -1;
      }
      if (u < 0) w = 0;
    }
    // Suivi vertical : regard bas → paupière supérieure descend ; regard haut → yeux plus ouverts.
    const down = Math.max(0, -this.eyeOut.pitch) / 0.4;
    const up = Math.max(0, this.eyeOut.pitch) / 0.4;
    const lid = Math.min(1, w + down * 0.35);
    this.face.setLayer('blink', { eyeBlinkLeft: lid, eyeBlinkRight: lid, eyeWideLeft: up * 0.25, eyeWideRight: up * 0.25 });
  }
}
